// invoice_list_v1: Invoice list view with stats, filter, table/cards
// Pattern follows HotGames.js (mobile responsive, color theme, helpers)
import React, { useState, useEffect } from 'react';
import API from '../api';
import {
  FileText, Plus, Search, RefreshCw, Eye, Edit2, Trash2,
  CheckCircle, Send, AlertCircle, XCircle, Clock,
} from 'lucide-react';
import toast from 'react-hot-toast';
import InvoiceForm from './InvoiceForm'; // invoice_form_wire_v1
import InvoiceDetail from './InvoiceDetail'; // invoice_detail_wire_v1

// ---------- helpers ----------
const STATUS_META = {
  draft:     { label: 'ร่าง',       color: '#64748b', bg: '#f1f5f9', icon: Clock },
  sent:      { label: 'ส่งแล้ว',    color: '#0284c7', bg: '#e0f2fe', icon: Send },
  paid:      { label: 'ชำระแล้ว',   color: '#16a34a', bg: '#dcfce7', icon: CheckCircle },
  overdue:   { label: 'ค้างชำระ',   color: '#dc2626', bg: '#fee2e2', icon: AlertCircle },
  cancelled: { label: 'ยกเลิก',     color: '#94a3b8', bg: '#f1f5f9', icon: XCircle },
};

const STATUS_ORDER = ['draft', 'sent', 'paid', 'overdue', 'cancelled'];

function formatDateTH(d) {
  if (!d) return '-';
  const date = new Date(d);
  if (isNaN(date)) return '-';
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yyyy = date.getFullYear();
  return dd + '/' + mm + '/' + yyyy;
}

function formatMoney(n, currency) {
  const num = Number(n) || 0;
  const cur = currency || 'THB';
  if (cur === 'USDT') return num.toFixed(2) + ' USDT';
  return num.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' ฿';
}

// ---------- main component ----------
export default function Invoices() {
  const [list, setList] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  // invoice_form_wire_v1: modal control
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  // invoice_detail_wire_v1: detail modal control
  const [detailOpen, setDetailOpen] = useState(false);
  const [viewingId, setViewingId] = useState(null);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const loadStats = async () => {
    try {
      const { data } = await API.get('/api/invoices/stats/summary');
      setStats(data);
    } catch (e) {
      console.error('Stats load error:', e);
    }
  };

  const loadList = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (search.trim()) params.append('search', search.trim());
      params.append('limit', '100');
      const { data } = await API.get('/api/invoices?' + params.toString());
      setList(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error('List load error:', e);
      toast.error('โหลดบิลไม่สำเร็จ');
      setList([]);
    }
    setLoading(false);
  };

  // Initial + filter changes
  useEffect(() => { loadStats(); }, []);
  useEffect(() => {
    const t = setTimeout(loadList, search ? 350 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, search]);

  const refreshAll = () => { loadStats(); loadList(); };

  // invoice_form_wire_v1
  const handleCreate = () => {
    setEditingId(null);
    setModalOpen(true);
  };

  // invoice_detail_wire_v1
  const handleView = (inv) => {
    setViewingId(inv.id);
    setDetailOpen(true);
  };

  // invoice_form_wire_v1
  const handleEdit = (inv) => {
    if (inv.status !== 'draft') {
      toast.error('แก้ไขได้เฉพาะบิลสถานะ "ร่าง" เท่านั้น');
      return;
    }
    setEditingId(inv.id);
    setModalOpen(true);
  };

  const handleDelete = async (inv) => {
    if (inv.status === 'paid') {
      toast.error('ไม่สามารถลบบิลที่ชำระแล้วได้');
      return;
    }
    if (!window.confirm('ลบบิล ' + inv.invoice_number + ' ใช่หรือไม่?')) return;
    try {
      await API.delete('/api/invoices/' + inv.id);
      toast.success('ลบบิลสำเร็จ');
      refreshAll();
    } catch (e) {
      toast.error(e?.response?.data?.error || 'ลบบิลไม่สำเร็จ');
    }
  };

  // ---------- render ----------
  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px', background:'#f8fafc', minHeight:'100%' }}>
      {/* Header */}
      <div style={{
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent:'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap: 10 }}>
          <FileText size={isMobile ? 22 : 26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin:0, fontSize: isMobile ? 18 : 22, fontWeight:700, color:'#1e293b' }}>จัดการบิล</h1>
            <p style={{ margin:0, fontSize: isMobile ? 12 : 13, color:'#64748b' }}>
              ออกบิล PromptPay / Bank / USDT ส่งให้ลูกค้าและติดตามสถานะ
            </p>
          </div>
        </div>
        <button onClick={handleCreate}
          style={{
            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
            background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
            padding: isMobile ? '12px 18px' : '10px 18px',
            fontSize: isMobile ? 14 : 13, fontWeight:600, cursor:'pointer',
          }}>
          <Plus size={isMobile ? 16 : 15} />สร้างบิล
        </button>
      </div>

      {/* Stats Cards (5 columns desktop / 2 cols mobile) */}
      <div style={{
        display:'grid',
        gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)',
        gap: isMobile ? 10 : 14,
        marginBottom: isMobile ? 16 : 20,
      }}>
        {STATUS_ORDER.map(st => {
          const meta = STATUS_META[st];
          const Icon = meta.icon;
          const data = stats ? stats[st] : { count: 0, total: 0 };
          return (
            <div key={st}
              onClick={() => setStatusFilter(statusFilter === st ? '' : st)}
              style={{
                background:'#fff', padding: isMobile ? 12 : 16,
                borderRadius: 12,
                border: '2px solid ' + (statusFilter === st ? meta.color : 'transparent'),
                boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
                cursor:'pointer',
                transition: 'border-color 120ms',
              }}>
              <div style={{ display:'flex', alignItems:'center', gap:6, marginBottom: 8 }}>
                <Icon size={14} color={meta.color} />
                <span style={{ fontSize: isMobile ? 11 : 12, fontWeight:600, color: meta.color }}>
                  {meta.label}
                </span>
              </div>
              <div style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, color:'#1e293b', lineHeight: 1.1 }}>
                {data.count || 0}
              </div>
              <div style={{ fontSize: isMobile ? 11 : 12, color:'#64748b', marginTop: 4 }}>
                {formatMoney(data.total, 'THB')}
              </div>
            </div>
          );
        })}
      </div>

      {/* Filter Bar */}
      <div style={{
        background:'#fff', padding: isMobile ? 12 : 14, borderRadius: 12,
        boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
        marginBottom: isMobile ? 12 : 16,
        display:'flex',
        flexDirection: isMobile ? 'column' : 'row',
        gap: 10, alignItems: 'stretch',
      }}>
        <div style={{ position:'relative', flex: 1 }}>
          <Search size={16} color="#94a3b8" style={{ position:'absolute', left: 12, top:'50%', transform:'translateY(-50%)' }} />
          <input
            type="text" placeholder="ค้นหา ลูกค้า หรือ เลขที่บิล..."
            value={search} onChange={e => setSearch(e.target.value)}
            style={{
              width: '100%', boxSizing:'border-box',
              padding: isMobile ? '12px 14px 12px 36px' : '9px 12px 9px 36px',
              borderRadius: 8, border: '1px solid #e2e8f0',
              fontSize: isMobile ? 16 : 14, outline:'none',
            }} />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{
            padding: isMobile ? '12px 14px' : '9px 12px',
            borderRadius: 8, border: '1px solid #e2e8f0',
            fontSize: isMobile ? 16 : 14, outline:'none',
            background:'#fff', cursor:'pointer',
            minWidth: isMobile ? '100%' : 160,
          }}>
          <option value="">ทุกสถานะ</option>
          {STATUS_ORDER.map(s => (
            <option key={s} value={s}>{STATUS_META[s].label}</option>
          ))}
        </select>
        <button onClick={refreshAll}
          style={{
            display:'flex', alignItems:'center', justifyContent:'center', gap:6,
            padding: isMobile ? '12px 18px' : '9px 16px',
            background:'#f1f5f9', color:'#64748b', border:'none', borderRadius: 8,
            cursor:'pointer', fontSize: isMobile ? 14 : 13, fontWeight:500,
          }}>
          <RefreshCw size={14} />รีเฟรช
        </button>
      </div>

      {/* List body */}
      <div style={{ background:'#fff', borderRadius: 12, boxShadow:'0 1px 6px rgba(0,0,0,0.05)', overflow:'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign:'center', color:'#94a3b8', fontSize: 14 }}>
            กำลังโหลด...
          </div>
        ) : list.length === 0 ? (
          <div style={{ padding: 60, textAlign:'center', color:'#94a3b8' }}>
            <div style={{ fontSize: 48, marginBottom: 12, opacity: 0.5 }}>📄</div>
            <div style={{ fontSize: 16, fontWeight: 600, color:'#64748b', marginBottom: 6 }}>
              {search || statusFilter ? 'ไม่พบบิลที่ตรงกับเงื่อนไข' : 'ยังไม่มีบิล'}
            </div>
            <div style={{ fontSize: 13, color:'#94a3b8' }}>
              {search || statusFilter ? 'ลองล้างตัวกรองดู' : 'คลิก "สร้างบิล" เพื่อเริ่มต้น'}
            </div>
          </div>
        ) : isMobile ? (
          /* Mobile: cards */
          <div>
            {list.map((inv, idx) => (
              <InvoiceCard
                key={inv.id} inv={inv}
                isLast={idx === list.length - 1}
                onView={handleView} onEdit={handleEdit} onDelete={handleDelete} />
            ))}
          </div>
        ) : (
          /* Desktop: table */
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background:'#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                  <Th>เลขที่บิล</Th>
                  <Th>ลูกค้า</Th>
                  <Th>วันที่ออก</Th>
                  <Th>กำหนดชำระ</Th>
                  <Th align="right">ยอดรวม</Th>
                  <Th align="center">สถานะ</Th>
                  <Th align="center">จัดการ</Th>
                </tr>
              </thead>
              <tbody>
                {list.map(inv => {
                  const meta = STATUS_META[inv.status] || STATUS_META.draft;
                  return (
                    <tr key={inv.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <Td>
                        <div style={{ fontWeight: 600, color:'#0ea5e9' }}>{inv.invoice_number}</div>
                      </Td>
                      <Td>
                        <div style={{ fontWeight: 500, color:'#1e293b' }}>{inv.recipient_name || '-'}</div>
                        {inv.recipient_phone && (
                          <div style={{ fontSize: 11, color:'#94a3b8', marginTop: 2 }}>{inv.recipient_phone}</div>
                        )}
                      </Td>
                      <Td>{formatDateTH(inv.issue_date)}</Td>
                      <Td>
                        <span style={{
                          color: inv.status === 'overdue' ? '#dc2626' : '#64748b',
                          fontWeight: inv.status === 'overdue' ? 600 : 400,
                        }}>
                          {formatDateTH(inv.due_date)}
                        </span>
                      </Td>
                      <Td align="right">
                        <div style={{ fontWeight: 600, color:'#1e293b' }}>
                          {formatMoney(inv.total_amount, inv.currency)}
                        </div>
                        {inv.total_amount_usdt != null && inv.currency !== 'USDT' && (
                          <div style={{ fontSize: 11, color:'#94a3b8', marginTop: 2 }}>
                            ≈ {Number(inv.total_amount_usdt).toFixed(2)} USDT
                          </div>
                        )}
                      </Td>
                      <Td align="center">
                        <span style={{
                          display:'inline-flex', alignItems:'center', gap: 4,
                          background: meta.bg, color: meta.color,
                          borderRadius: 6, padding: '4px 10px',
                          fontSize: 12, fontWeight: 600,
                        }}>
                          {meta.label}
                        </span>
                      </Td>
                      <Td align="center">
                        <div style={{ display:'inline-flex', gap: 4 }}>
                          <IconBtn title="ดู" onClick={() => handleView(inv)} color="#0ea5e9"><Eye size={14} /></IconBtn>
                          <IconBtn title="แก้ไข" onClick={() => handleEdit(inv)} color="#64748b"><Edit2 size={14} /></IconBtn>
                          <IconBtn title="ลบ" onClick={() => handleDelete(inv)} color="#ef4444"><Trash2 size={14} /></IconBtn>
                        </div>
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Count footer */}
      {!loading && list.length > 0 && (
        <div style={{ marginTop: 12, fontSize: 12, color:'#94a3b8', textAlign:'center' }}>
          แสดง {list.length} รายการ {statusFilter ? '(กรองตาม: ' + STATUS_META[statusFilter].label + ')' : ''}
        </div>
      )}

      {/* invoice_form_wire_v1: Create/Edit modal */}
      <InvoiceForm
        open={modalOpen}
        editId={editingId}
        onClose={() => { setModalOpen(false); setEditingId(null); }}
        onSaved={() => { refreshAll(); }}
      />

      {/* invoice_detail_wire_v1: View detail modal */}
      <InvoiceDetail
        open={detailOpen}
        invoiceId={viewingId}
        onClose={() => { setDetailOpen(false); setViewingId(null); }}
        onChanged={() => { refreshAll(); }}
        onEdit={(id) => { setEditingId(id); setModalOpen(true); }}
      />
    </div>
  );
}

// ---------- subcomponents ----------
function InvoiceCard({ inv, isLast, onView, onEdit, onDelete }) {
  const meta = STATUS_META[inv.status] || STATUS_META.draft;
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
    }}>
      {/* Top row: invoice number + status */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom: 8, gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color:'#0ea5e9' }}>{inv.invoice_number}</div>
          <div style={{ fontSize: 13, fontWeight: 500, color:'#1e293b', marginTop: 2, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
            {inv.recipient_name || '-'}
          </div>
          {inv.recipient_phone && (
            <div style={{ fontSize: 11, color:'#94a3b8', marginTop: 2 }}>{inv.recipient_phone}</div>
          )}
        </div>
        <span style={{
          background: meta.bg, color: meta.color,
          borderRadius: 6, padding: '4px 10px',
          fontSize: 11, fontWeight: 600, flexShrink: 0,
        }}>
          {meta.label}
        </span>
      </div>

      {/* Dates + amount */}
      <div style={{ background:'#f8fafc', padding: 10, borderRadius: 8, marginBottom: 10 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 4 }}>
          <span style={{ fontSize: 11, color:'#64748b' }}>ออกบิล:</span>
          <span style={{ fontSize: 12, color:'#1e293b', fontWeight: 500 }}>{formatDateTH(inv.issue_date)}</span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 6 }}>
          <span style={{ fontSize: 11, color:'#64748b' }}>กำหนดชำระ:</span>
          <span style={{
            fontSize: 12, fontWeight: 500,
            color: inv.status === 'overdue' ? '#dc2626' : '#1e293b',
          }}>
            {formatDateTH(inv.due_date)}
          </span>
        </div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', borderTop: '1px solid #e2e8f0', paddingTop: 6 }}>
          <span style={{ fontSize: 12, color:'#64748b', fontWeight: 500 }}>ยอดรวม:</span>
          <div style={{ textAlign:'right' }}>
            <div style={{ fontSize: 15, fontWeight: 700, color:'#1e293b' }}>
              {formatMoney(inv.total_amount, inv.currency)}
            </div>
            {inv.total_amount_usdt != null && inv.currency !== 'USDT' && (
              <div style={{ fontSize: 10, color:'#94a3b8' }}>
                ≈ {Number(inv.total_amount_usdt).toFixed(2)} USDT
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Actions */}
      <div style={{ display:'flex', gap: 6 }}>
        <button onClick={() => onView(inv)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background:'#e0f2fe', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 4,
            fontSize: 13, color:'#0284c7', fontWeight: 500,
          }}>
          <Eye size={14} />ดู
        </button>
        <button onClick={() => onEdit(inv)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background:'#f1f5f9', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center', gap: 4,
            fontSize: 13, color:'#64748b', fontWeight: 500,
          }}>
          <Edit2 size={14} />แก้ไข
        </button>
        <button onClick={() => onDelete(inv)}
          style={{
            padding: '10px 14px', borderRadius: 8,
            background:'#fff1f2', border:'none', cursor:'pointer',
            display:'flex', alignItems:'center', justifyContent:'center',
          }}>
          <Trash2 size={14} color="#ef4444" />
        </button>
      </div>
    </div>
  );
}

const Th = ({ children, align }) => (
  <th style={{
    padding: '12px 14px',
    textAlign: align || 'left',
    fontSize: 12, fontWeight: 600, color:'#64748b',
    textTransform: 'uppercase', letterSpacing: '0.03em',
  }}>{children}</th>
);

const Td = ({ children, align }) => (
  <td style={{
    padding: '12px 14px',
    textAlign: align || 'left',
    color:'#334155',
    verticalAlign: 'middle',
  }}>{children}</td>
);

const IconBtn = ({ children, onClick, color, title }) => (
  <button onClick={onClick} title={title}
    style={{
      padding: 6, borderRadius: 6,
      background: 'transparent', border: '1px solid #e2e8f0',
      cursor:'pointer', color,
      display:'inline-flex', alignItems:'center', justifyContent:'center',
    }}>
    {children}
  </button>
);
